import React, { useState } from 'react';
import { supabase } from '../lib/supabase';
import { useNavigate } from 'react-router-dom';

export default function Register() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Set document title and reset accent color
  React.useEffect(() => {
    document.title = 'Administratie';

    // Reset accent color to default blue when on register page
    document.documentElement.style.setProperty('--accent-color', '#3B82F6');
    document.documentElement.style.setProperty('--accent-color-hover', '#2563EB');
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    if (password !== confirmPassword) {
      setError('Wachtwoorden komen niet overeen');
      setLoading(false);
      return;
    }

    try {
      // Register the user
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email,
        password,
      });

      if (authError) throw authError;

      if (authData.user) {
        // Create initial settings for the user
        const { error: settingsError } = await supabase
          .from('settings')
          .insert([
            {
              user_id: authData.user.id,
              company_name: companyName,
              owner_name: '',
              email: email,
              phone: '',
              address: '',
              postal_code: '',
              city: '',
              weekly_revenue_goal: 1000,
            }
          ])
          .select()
          .maybeSingle();

        if (settingsError) throw settingsError;

        // Show success message and navigate to login
        setError('Registratie succesvol! Je kunt nu inloggen.');
        setTimeout(() => {
          navigate('/login');
        }, 2000);
      }
    } catch (error: any) {
      console.error('Registration error:', error);
      setError(error.message === 'User already registered'
        ? 'Dit e-mailadres is al geregistreerd'
        : 'Er is een fout opgetreden bij het registreren');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <p className="text-gray-400 text-sm">Maak een nieuw account aan</p>
        </div>
        <div className="bg-gray-800 rounded-xl border border-gray-700 p-8 shadow-xl">

          <form className="space-y-5" onSubmit={handleSubmit}>
            {error && (
              <div className={`p-3 rounded-lg text-sm flex items-center justify-center border ${
                error.includes('succesvol')
                  ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20'
                  : 'bg-red-500/10 text-red-400 border-red-500/20'
              }`}>
                {error}
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label
                  htmlFor="companyName"
                  className="block text-sm font-medium text-gray-300 mb-1.5"
                >
                  Bedrijfsnaam
                </label>
                <input
                  id="companyName"
                  type="text"
                  required
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  className="block w-full rounded-lg border border-gray-700 px-3.5 py-2.5
                    bg-gray-800 text-white
                    placeholder-gray-500
                    focus:border-accent focus:bg-gray-700 focus:ring-1 focus:ring-accent
                    focus:outline-none
                    transition-all duration-200"
                  placeholder="Uw bedrijfsnaam"
                />
              </div>

              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-medium text-gray-300 mb-1.5"
                >
                  E-mailadres
                </label>
                <input
                  id="email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="block w-full rounded-lg border border-gray-700 px-3.5 py-2.5
                    bg-gray-800 text-white
                    placeholder-gray-500
                    focus:border-accent focus:bg-gray-700 focus:ring-1 focus:ring-accent
                    focus:outline-none
                    transition-all duration-200"
                  placeholder="naam@bedrijf.nl"
                />
              </div>

              <div>
                <label
                  htmlFor="password"
                  className="block text-sm font-medium text-gray-300 mb-1.5"
                >
                  Wachtwoord
                </label>
                <input
                  id="password"
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="block w-full rounded-lg border border-gray-700 px-3.5 py-2.5
                    bg-gray-800 text-white
                    placeholder-gray-500
                    focus:border-accent focus:bg-gray-700 focus:ring-1 focus:ring-accent
                    focus:outline-none
                    transition-all duration-200"
                  placeholder="••••••••"
                />
              </div>

              <div>
                <label
                  htmlFor="confirmPassword"
                  className="block text-sm font-medium text-gray-300 mb-1.5"
                >
                  Bevestig wachtwoord
                </label>
                <input
                  id="confirmPassword"
                  type="password"
                  required
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="block w-full rounded-lg border border-gray-700 px-3.5 py-2.5
                    bg-gray-800 text-white
                    placeholder-gray-500
                    focus:border-accent focus:bg-gray-700 focus:ring-1 focus:ring-accent
                    focus:outline-none
                    transition-all duration-200"
                  placeholder="••••••••"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full flex items-center justify-center px-4 py-3
                rounded-lg text-base font-medium text-white bg-accent hover:bg-accent-hover
                focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 focus:ring-offset-gray-900
                disabled:opacity-50 disabled:cursor-not-allowed
                transition-colors duration-200"
            >
              {loading ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  Registreren...
                </div>
              ) : (
                'Registreren'
              )}
            </button>

            <div className="text-center mt-5">
              <p className="text-sm text-gray-400">
                Al een account?{' '}
                <button
                  type="button"
                  onClick={() => navigate('/login')}
                  className="text-accent font-medium hover:text-accent-hover focus:outline-none transition-colors"
                >
                  Log hier in
                </button>
              </p>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}